import { useEffect, useMemo, useState } from "react";
import { Box, Tabs, Tab, Typography, Alert, CircularProgress, Button, Snackbar } from "@mui/material";
import { SettingsPanel } from "@/components/SettingsPanel";
import { FeaturePanel } from "@/components/FeaturePanel";
import { loadConfig, isConfigured } from "@/store/config";
import { hidePanel } from "@/office/integration";
import type { FeatureId } from "@/types";

type TabId = FeatureId | "settings";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "draft", label: "Draft" },
  { id: "translate", label: "Translate" },
  { id: "summarize", label: "Summarize" },
  { id: "tone", label: "Tone" },
  { id: "proofread", label: "Proofread" },
  { id: "settings", label: "⚙ Settings" },
];

export default function App() {
  const [tab, setTab] = useState<TabId>("draft");
  const [configVersion, setConfigVersion] = useState(0);
  const cfg = useMemo(() => loadConfig(), [configVersion]);
  const configured = isConfigured(cfg);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (tab === "settings") setConfigVersion((v) => v + 1);
  }, [tab]);

  async function handleHide() {
    const r = await hidePanel();
    if (!r.ok) setHint(r.hint ?? "Could not hide the task pane.");
  }

  return (
    <Box sx={{ p: 1.5, height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">bishu · LLM for Outlook</Typography>
        <Button size="small" variant="text" onClick={handleHide} sx={{ textTransform: "none", minWidth: 0 }}>
          Hide panel
        </Button>
      </Box>

      {!configured && tab !== "settings" && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          No LLM endpoint configured yet. Open Settings to add one.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabId)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40 }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.id}
            value={t.id}
            label={t.label}
            sx={{ minHeight: 40, textTransform: "none", minWidth: 72 }}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, overflow: "auto", mt: 1 }}>
        {tab === "settings" && (
          <SettingsPanel onSaved={() => setConfigVersion((v) => v + 1)} />
        )}
        {tab !== "settings" && (
          configured ? (
            <FeaturePanel feature={tab as FeatureId} config={cfg} />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Configure an LLM endpoint in Settings first.
              </Typography>
            </Box>
          )
        )}
      </Box>

      <Snackbar
        open={hint !== null}
        autoHideDuration={6000}
        onClose={() => setHint(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={hint}
      />
    </Box>
  );
}
